import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { HelpText } from '../../../model/help-text';
import { IsTouchOnlyEnvironment } from '../../../helper/mobile';
import { FormControlEx } from '../../forms/form-control';
import { AstralPopoverComponent } from '../astral-popover/astral-popover.component';
import { AstralLabelOverflowDirective } from '../astral-label-overflow.directive';
import { shouldShowAstralValidationError } from '../astral-validation-state';
import { getAstralFieldErrorId, getAstralFieldHelpId, getAstralFieldLabelId } from '../astral-field-accessibility.util';

type AstralFieldError = {
  message: string;
  params?: any;
};

@Component({
  selector: 'astral-field-shell',
  standalone: true,
  imports: [TranslatePipe, AstralPopoverComponent, AstralLabelOverflowDirective],
  template: `
    <div
      class="astral-field-shell"
      [class.astral-field-shell--invalid]="resolvedInvalid"
      [class.astral-field-shell--readonly]="resolvedReadonly"
      [class.astral-field-shell--disabled]="resolvedDisabled"
    >
      @if (showHeader) {
      <div class="astral-field-shell__header">
        <div class="astral-field-shell__header-main">
          @if (showLabel) {
          @if (associateLabel) {
          <label class="astral-field-shell__label" [attr.id]="labelId" [attr.for]="resolvedForId || null">
            <span astralLabelOverflow class="astral-field-shell__label-text" (astralLabelOverflowChange)="labelTruncated = $event">{{ resolvedLabel | translate }}</span>
            @if (resolvedRequired && showRequiredMarker) {
            <span class="astral-field-shell__required" aria-hidden="true">*</span>
            }
          </label>
          } @else {
          <span class="astral-field-shell__label" [attr.id]="labelId">
            <span astralLabelOverflow class="astral-field-shell__label-text" (astralLabelOverflowChange)="labelTruncated = $event">{{ resolvedLabel | translate }}</span>
            @if (resolvedRequired && showRequiredMarker) {
            <span class="astral-field-shell__required" aria-hidden="true">*</span>
            }
          </span>
          }
          }

          @if (labelTruncated) { <astral-popover class="astral-field-shell__label-overflow" [helpText]="resolvedLabel" [triggerIcon]="'ellipsis'" [triggerLabelKey]="labelOverflowTriggerLabelKey" [inline]="true"></astral-popover> }
          @if (resolvedHelpText) { <astral-popover [helpText]="resolvedHelpText" [contentId]="helpId" [inline]="true"></astral-popover> }
        </div>
      </div>
      }

      <div class="astral-field-shell__control">
        <ng-content></ng-content>
      </div>

      @if (resolvedInvalid && resolvedErrorMessage) {
      <div class="astral-field-shell__error" [attr.id]="errorId" role="alert">
        {{ resolvedErrorMessage.message | translate: resolvedErrorMessage.params }}
      </div>
      }
    </div>
  `,
  styleUrls: ['./astral-field-shell.component.scss'],
})
export class AstralFieldShellComponent implements OnDestroy {
  private controlSubscription = new Subscription();
  private _control: FormControlEx<any>;
  labelTruncated = false;

  constructor(private readonly cd: ChangeDetectorRef) {}

  @Input() set control(value: FormControlEx<any>) {
    this.controlSubscription.unsubscribe();
    this.controlSubscription = new Subscription();
    this._control = value;

    if (!value) {
      return;
    }

    this.controlSubscription.add(value.statusChanges.subscribe(() => this.cd.markForCheck()));
    this.controlSubscription.add(value.valueChanges.subscribe(() => this.cd.markForCheck()));
    this.controlSubscription.add(value.touchStateChanged?.subscribe(() => this.cd.markForCheck()));
    this.controlSubscription.add(value.validatorsChanged?.subscribe(() => this.cd.markForCheck()));
    this.cd.markForCheck();
  }
  get control(): FormControlEx<any> {
    return this._control;
  }
  @Input() disabled: boolean;
  @Input() associateLabel = true;
  @Input() errorMessage: AstralFieldError;
  @Input() forId: string;
  @Input() helpText: string | HelpText;
  @Input() hideLabel = false;
  @Input() invalid: boolean;
  @Input() label: string;
  @Input() readonly: boolean;
  @Input() required: boolean;
  @Input() showRequiredMarker = true;

  get resolvedDisabled(): boolean {
    return this.disabled ?? this.control?.disabled ?? false;
  }

  get errorId(): string | null {
    return this.forId ? getAstralFieldErrorId(this.forId) : null;
  }

  get helpId(): string | null {
    return this.forId ? getAstralFieldHelpId(this.forId) : null;
  }

  get labelId(): string | null {
    return this.forId ? getAstralFieldLabelId(this.forId) : null;
  }

  get resolvedErrorMessage(): AstralFieldError {
    if (this.errorMessage) {
      return this.errorMessage;
    }

    if (!this.control?.getErrorMessage) {
      return null;
    }

    return this.control.getErrorMessage();
  }

  get resolvedForId(): string | null {
    if (this.forId) {
      return this.forId;
    }

    const source = this.resolvedLabel;
    if (!source) {
      return null;
    }

    return (
      source
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || null
    );
  }

  get resolvedHelpText(): string | null {
    const helpText = this.helpText ?? this.control?.helpText;
    if (!helpText) {
      return null;
    }

    if (typeof helpText === 'string') {
      return helpText;
    }

    return helpText.text ?? null;
  }

  get resolvedInvalid(): boolean {
    if (this.invalid !== undefined && this.invalid !== null) {
      return this.invalid;
    }

    return shouldShowAstralValidationError(this.control);
  }

  get resolvedLabel(): string {
    return this.label ?? this.control?.caption ?? '';
  }

  get labelOverflowTriggerLabelKey(): string {
    return IsTouchOnlyEnvironment() ? 'COMMON.TAP_TO_SHOW_FULL_FIELD_NAME' : 'COMMON.CLICK_TO_SHOW_FULL_FIELD_NAME';
  }

  get resolvedReadonly(): boolean {
    return this.readonly ?? this.control?.isReadonly ?? false;
  }

  get resolvedRequired(): boolean {
    return this.required ?? this.control?.required ?? false;
  }

  get showHeader(): boolean {
    return this.showLabel || !!this.resolvedHelpText;
  }

  get showLabel(): boolean {
    return !this.hideLabel && !!this.resolvedLabel;
  }

  ngOnDestroy(): void {
    this.controlSubscription.unsubscribe();
  }
}
